<template>
  <b-card no-body>
    <b-card-body>
      <department-list-filter
        :values="filterValues"
        :filter-submitting="isLoading"
        @filter="setFilter"
      />

      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        :rows="rows"
        :columns="getColumns"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'name'">
            {{ props.row.name || '-' }}
          </span>
          <span v-if="props.column.field === 'country.name'">
            {{ props.row.country.name || '-' }}
          </span>
          <span v-else-if="props.column.field === 'address'">
            {{ props.row.address || '-' }}
          </span>
          <span v-else-if="props.column.field === 'postalCode'">
            {{ props.row.postalCode || '-' }}
          </span>
          <span v-else-if="props.column.field === 'phoneNumber'">
            {{ props.row.phoneNumber || '-' }}
          </span>
          <span v-else-if="props.column.field === 'email'">
            {{ props.row.email || '-' }}
          </span>
          <span v-if="props.column.field === 'status'">
            <b-badge pill class="text-white" :variant="props.row.status ? 'success' : 'danger'">
              {{ props.row.status ? t('Active') : t('Passive') }}
            </b-badge>
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="editDepartment(props.row.id)"
              >
                <feather-icon icon="Edit2Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="$t('Edit Department')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>

              <b-button
                :id="`delete-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="deleteDepartment(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`delete-button-${props.row.id}`"
                  :title="$t('Delete Department')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
              <b-dropdown size="sm" variant="secondary" right dropup stop>
                <template #button-content>
                  <feather-icon icon="settings" size="16" class="mr-1" />
                  <b>{{ t('Others') }}</b>
                </template>
                <b-dropdown-item @click="getAllRuleList(props.row.id)">
                  {{ t('Department Rule Sets') }}
                </b-dropdown-item>
              </b-dropdown>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BBadge,
  BCard,
  BCardBody,
  BTooltip,
  BButton,
  BDropdown,
  BDropdownItem
} from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import DepartmentListFilter from '@/views/pages/departments/DepartmentListFilter.vue';
import useService from '@/auth/service/useService';
import store from '@/store/index';

export default {
  components: {
    VueGoodTable,
    BBadge,
    BCard,
    BCardBody,
    BTooltip,
    BButton,
    BDropdown,
    BDropdownItem,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    DepartmentListFilter
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      rows: [],
      details: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        name: '',
        address: '',
        postalCode: '',
        phoneNumber: '',
        email: ''
      },
      defaultFilterValues: {
        name: '',
        address: '',
        postalCode: '',
        phoneNumber: '',
        email: ''
      }
    };
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Name'),
          field: 'name'
        },
        {
          label: this.$t('Country'),
          field: 'country.name'
        },
        {
          label: this.$t('Address'),
          field: 'address'
        },
        {
          label: this.$t('Postal Code'),
          field: 'postalCode'
        },
        {
          label: this.$t('Phone Number'),
          field: 'phoneNumber'
        },
        {
          label: this.$t('Email'),
          field: 'email'
        },
        {
          label: this.$t('Status'),
          field: 'status'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },
  async created() {
    this.loadItems();
  },
  methods: {
    async getAllRuleList(id) {
      await this.$router.push({ name: 'departments_rules', params: { id } });
    },
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    // load items is what brings back the rows from server
    loadItems() {
      let requestData = this.filter;

      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      if (
        requestData.dateFrom &&
        requestData.dateTo &&
        new Date(requestData.dateFrom) > new Date(requestData.dateTo)
      ) {
        this.showErrorMessage(this.$t('Big Date Error'), this.$t('Unsuccessful'));
        return;
      }

      this.isLoading = true;
      useService
        .getAllDepartments(requestData)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setFilter(data) {
      this.filterValues = data || this.defaultFilterValues;
      this.filter.page = 1;
      this.loadItems();
    },
    editDepartment(id) {
      this.$router.push({ name: 'edit_department', params: { id } });
    },
    deleteDepartment(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Delete Department Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await useService.deleteDepartment(id).then(response => {
            this.showSuccessMessage(
              `${response.message || this.$t('Delete Department Success Message')}`
            );

            const index = this.rows.findIndex(item => item.id === id);
            this.rows.splice(index, 1);
          });
        }
      });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
